/* global chrome, cast */ // The Cast sender SDK, loaded by its own script tag.
import { Controller } from "stimulus";
import mux from "mux-embed";
import { SupercastTimeUpdateEvent } from "utils/events";
import { maybeConnectBridge } from "utils/hotwire";
import { trackPlaybackStarted } from "utils/playback_analytics";
import "video_skin";
import { sendPlaybackBreadcrumb, sendPlaybackError } from "utils/playback_telemetry";
import { getAudioPlayerController } from "utils/audio_player";
import { readPlyrStorage, writePlyrStorage, nearestRate } from "utils/player_preferences";
import { claimMediaSession, ownsMediaSession, releaseMediaSession } from "utils/media_session_owner";

// Video.js v10 player controller for the web video player.
// It plays VOD/live HLS and keeps the native bridge hand-off and the public API other controllers call.
//
// The Video.js element registration is imported statically, so <video-player> is defined by the same
// bundle that registers this controller.
//
// <hlsjs-video> is not an HTMLMediaElement, and its real <video> lives in its shadow root. The
// element proxies the media API (play, paused, currentTime, error, media events), so `this.video`
// returns the <hlsjs-video> element itself.

/** WARNING: This controller has bridge component counterparts, which the mobile apps handle
 * natively. Keep both in sync. */

const READY_POLL_MS = 50;

// Which playback branch produced the Mux view. Names are stable so the Mux
// dashboards keep one vocabulary.
const muxBranchHlsJs = "hlsjs";
const muxBranchNativeHls = "native-hls";
const muxBranchProgressive = "progressive-mp4";

const MEDIA_SESSION_EVENTS = ["play", "pause", "ended", "seeked", "ratechange", "loadedmetadata", "durationchange"];

// Off until caption CDN CORS/auth for the receiver is confirmed.
const CAST_CAPTIONS_ENABLED = false;

// Short reason for a Cast failure: the framework error code or message only, never URLs or requests.
const castFailureReason = (error) => {
  if (typeof error === "string") return error;
  return error?.code || error?.description || error?.message || "unknown";
};

// How long a restored caption track may take to show up in the store before the restore is abandoned.
const CAPTIONS_RESTORE_TIMEOUT_MS = 3000;

// MediaError.code 1: the load was interrupted on purpose (a new source replaced it), not a failure.
const MEDIA_ERR_ABORTED = 1;

// AWS HLS only (see awsHlsValue): the master manifest only lists rungs that have finished encoding,
// and hls.js never re-fetches a VOD master on its own, so a session that starts before every rung
// is done would not see a later one. Polling keeps the existing cadence and cap.
const MASTER_MANIFEST_POLL_INTERVAL_MS = 45_000;
const MASTER_MANIFEST_MAX_POLL_ATTEMPTS = 40; // ~30 minutes; stop rather than poll forever
// Live: lag still counted as live. Premiere: how far past the shared playhead a seek may land.
const LIVE_EDGE_TOLERANCE_SECONDS = 1;
const PREMIERE_EDGE_TOLERANCE_SECONDS = 1.5;
const MASTER_MANIFEST_RUNG_TOTAL = 5;
// iPhone native fullscreen: a pause this close to leaving it is WebKit's, not the viewer's.
const FULLSCREEN_EXIT_PAUSE_WINDOW_MS = 1000;
const FULLSCREEN_RESUME_MAX_ATTEMPTS = 3;

// m:ss, or h:mm:ss from an hour, for the premiere tip's screen reader description.
function formatClock(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}` : `${minutes}:${secs}`;
}

// The nearest accessor for `name` on the object's prototype chain (custom media elements define
// currentTime several prototypes up).
function findAccessorDescriptor(object, name) {
  for (let proto = Object.getPrototypeOf(object); proto; proto = Object.getPrototypeOf(proto)) {
    const descriptor = Object.getOwnPropertyDescriptor(proto, name);
    if (descriptor) return descriptor.get && descriptor.set ? descriptor : null;
  }
  return null;
}

export default class extends Controller {
  static targets = [
    "media",
    "liveElapsed",
    "videoContainer",
    "castPlaceholder",
    "castStatus",
    "castButton"
  ]

  static values = {
    timestamp: Number,
    markers: Array,
    fileType: String,
    autoplay: Boolean,
    preshow: Boolean,
    allowNativePlay: Boolean,
    cdn: String,
    videoTitle: String,
    videoId: String,
    streamType: String,
    liveUrl: String,
    liveOrigin: Number,
    muxEnvKey: String,
    publishedAtMs: Number,
    held: Boolean,
    serverNowMs: Number,
    duration: Number,
    awsHls: Boolean,
    castPayloadUrl: String,
    castEventId: Number,
    castContentType: String,
    observability: Boolean,
    viewerId: String,
    subPropertyId: String,
    planTier: String,
    tokenType: String,
    expiresAtMs: Number
  }

  initialize() {
    this.boundMediaError = () => this.handleMediaError();
    this.boundCanPlay = () => this.handleCanPlay();
    this.boundTimeUpdate = () => this.updateTime();
    this.boundRateChange = () => this.persistSpeed();
    this.boundLoadedMetadata = () => this.reapplySpeed();
  }

  async connect() {
    this.startLiveElapsed();
    if (this.allowNativePlayValue && maybeConnectBridge(this.element, this.bridgeComponent(), this.bridgeControllerName())) {
      return;
    }

    // Inside the native shell this player is CSS-hidden; when the hand-off didn't happen there is
    // nothing to show, so skip web init entirely.
    if (document.querySelector("[data-hotwire-native]")) {
      return;
    }

    this.connected = true;
    this.ready = false;

    // Snapshot the clock offset before the ready await so the delay isn't lost.
    if (this.isPremiere()) this.clockOffsetMs = this.serverNowMsValue - Date.now();

    this.muxPlayerInitTime = mux.utils.now();

    const ready = await this.whenPlayerReady();
    if (!ready) return;

    this.ready = true;
    trackPlaybackStarted(this.media, this.isLive() ? "live" : this.isPremiere() ? "premiere" : "episode");
    this.media.addEventListener("error", this.boundMediaError);
    this.media.addEventListener("canplay", this.boundCanPlay);
    // Preshow has no manifest yet (a 404 would open the error dialog); preshowValueChanged loads it.
    if (!this.preshowValue) this.startPlayback();
    this.setupSpeed();
    this.setupPreferences();
    this.setupProgress();
    this.setupMediaSession();
    this.setupWakeLock();
    this.setupOrientationLock();
    this.setupFullscreenResume();
    this.setupMiniAudioInterop();
    this.setupPictureInPicture();
    this.initChromecast();
    this.setupAirplayCastUrl();
    this.setupLive();
    this.setupPremiere();
    // TODO: embed fallback.
  }

  disconnect() {
    this.connected = false;
    this.sourceLoaded = false;
    this.ready = false;
    clearTimeout(this.readyTimer);
    this.readyTimer = null;
    this.media?.removeEventListener("loadedmetadata", this.applyTimestamp);
    this.media?.removeEventListener("error", this.boundMediaError);
    this.media?.removeEventListener("canplay", this.boundCanPlay);
    this.media?.removeEventListener("timeupdate", this.boundTimeUpdate);
    this.media?.removeEventListener("loadedmetadata", this.applyResume);
    this.stopLiveElapsed();
    this.teardownLive();
    this.teardownPremiere();
    this.progressActive = false;
    this.media?.removeEventListener("ratechange", this.boundRateChange);
    this.media?.removeEventListener("loadedmetadata", this.boundLoadedMetadata);
    clearTimeout(this.captionsRestoreTimer);
    this.captionsRestoreTimer = null;
    this.unsubscribePreferences?.();
    this.unsubscribePreferences = null;
    this.teardownMediaSession();
    this.teardownWakeLock();
    this.teardownOrientationLock();
    this.teardownFullscreenResume();
    clearTimeout(this.manifestPollTimer);
    this.manifestPollTimer = null;
    this.media?.removeEventListener("play", this.pauseMiniAudioPlayer);
    this.teardownCast();
    this.teardownForDisconnect();
  }

  // Picture-in-Picture does not survive navigation: it is exited first, then everything is torn down.
  teardownForDisconnect() {
    this.exitPictureInPictureForNavigation();
    // Tell Mux the view is ending deliberately BEFORE the source goes away (the adapter destroys
    // itself right after this element disconnects). Otherwise an ordinary Turbo navigation reads to
    // mux-embed's automatic error tracking as a MEDIA_ERR_SRC_NOT_SUPPORTED failure.
    this.muxTeardown();
  }

  // The player tag the template rendered, and the media element inside it.
  get playerElement() {
    return this.element.querySelector("video-player, live-video-player");
  }

  get media() {
    return this.hasMediaTarget ? this.mediaTarget : null;
  }

  // What outside callers read for "is there a playable element". Null until the player is ready,
  // and after a disconnect.
  get video() {
    return this.ready ? this.media : null;
  }

  // Live elapsed readout: time since go-live (same mm:ss as the banner timer), clamped to 0 before origin.
  // Idempotent: connect(), a go-live morph changing the origin, and a re-rendered target all call it.
  startLiveElapsed() {
    this.stopLiveElapsed();
    if (!this.isLive() || !this.hasLiveElapsedTarget) return;
    this.renderLiveElapsed();
    if (this.liveOriginValue > 0) this.liveElapsedTimer = window.setInterval(() => this.renderLiveElapsed(), 1000);
  }

  // A Turbo morph on go-live keeps this controller and only changes the origin value.
  liveOriginValueChanged() {
    this.startLiveElapsed();
  }

  // A morph can swap the readout element; paint (and tick) into the new one.
  liveElapsedTargetConnected() {
    this.startLiveElapsed();
  }

  stopLiveElapsed() {
    window.clearInterval(this.liveElapsedTimer);
    this.liveElapsedTimer = null;
  }

  renderLiveElapsed() {
    const totalSeconds = this.liveOriginValue > 0 ? Math.floor(Math.max(0, Date.now() - this.liveOriginValue) / 1000) : 0;
    const pad = (n) => String(n).padStart(2, "0");
    const text = `${pad(Math.floor(totalSeconds / 60))}:${pad(totalSeconds % 60)}`;
    this.liveElapsedTargets.forEach((el) => { el.textContent = text; });
  }

  isLive() {
    return this.streamTypeValue === "live";
  }

  isPremiere() {
    return this.streamTypeValue === "premiere";
  }

  // The bridge component name doubles as the native capability check: old app installs
  // don't declare the live/premiere names, so those pages keep today's behavior there.
  bridgeComponent() {
    if (this.isLive()) return "live-player";
    if (this.isPremiere()) return "premiere-player";
    return "video-player";
  }

  // The bridge controllers are glob-registered, so their Stimulus identifier is always
  // media--bridge--<component>.
  bridgeControllerName() {
    return `media--bridge--${this.bridgeComponent()}`;
  }

  // Resolves true once the custom elements are defined and the player store is attached to the
  // media (actions are not queued before that), false if the controller disconnected meanwhile.
  async whenPlayerReady() {
    const tag = this.playerElement?.localName;
    if (!tag || !this.media) return false;

    await customElements.whenDefined(tag);
    if (!this.connected) return false;

    return new Promise((resolve) => {
      const check = () => {
        if (!this.connected) return resolve(false);
        if (this.playerElement?.store?.target) return resolve(true);
        this.readyTimer = setTimeout(check, READY_POLL_MS);
      };
      check();
    });
  }

  // --- Speed ---

  // Speed lives in the same localStorage["plyr"] JSON the audio player uses. Only `speed` is touched.
  //
  // Assigning media.source runs the media load algorithm, which resets playbackRate to 1 (and fires a
  // ratechange). So the saved rate is applied up front for the UI, re-applied on loadedmetadata, and
  // ratechange events are ignored until then: only a rate the viewer picks on a loaded source is
  // persisted, never the reset to 1.
  setupSpeed() {
    if (this.isLive() || this.isPremiere()) {
      this.resetSpeedForLiveAndPremiere();
      return;
    }

    const stored = this.storedSpeed();
    this.intendedRate = stored || 1;
    this.sourceLoading = Boolean(this.hlsUrl());
    if (stored) this.setRate(stored);
    this.media.addEventListener("loadedmetadata", this.boundLoadedMetadata);
    this.media.addEventListener("ratechange", this.boundRateChange);
  }

  // For live and premiere: they have no speed control, so force 1x. Persistence is never
  // attached for them, so the viewer's stored VOD speed stays untouched and applies again on the next VOD.
  // The live player's feature set has no playback rate at all, hence the guard.
  resetSpeedForLiveAndPremiere() {
    this.media.removeEventListener("ratechange", this.boundRateChange);
    this.media.removeEventListener("loadedmetadata", this.boundLoadedMetadata);
    this.setRate(1);
  }

  // No-op where the player has no playback rate feature (live).
  setRate(rate) {
    const store = this.playerElement?.store;
    if (typeof store?.setPlaybackRate === "function") store.setPlaybackRate(rate);
  }

  // A new source is about to be assigned: its load resets the rate, so ignore ratechanges until metadata.
  markSourceLoading() {
    if (this.intendedRate) this.sourceLoading = true;
  }

  reapplySpeed() {
    if (!this.intendedRate) return;
    this.setRate(this.intendedRate);
    this.sourceLoading = false;
  }

  readPlyrStorage() {
    return readPlyrStorage();
  }

  // Restores the stored speed, snapped to the nearest rate this player offers.
  storedSpeed() {
    const speed = Number(this.readPlyrStorage()?.speed);
    return speed > 0 ? this.nearestSupportedRate(speed) : null;
  }

  nearestSupportedRate(speed) {
    return nearestRate(this.playerElement.store.state?.playbackRates, speed);
  }

  persistSpeed() {
    const speed = this.media?.playbackRate;
    if (!speed || this.sourceLoading || speed === this.intendedRate) return;

    this.intendedRate = speed;
    this.writePlyrStorage({ speed });
  }

  // The single write path: merges into the existing JSON so the audio player's keys survive, and never
  // writes `quality` (the other video player strips it so quality always starts on Auto).
  writePlyrStorage(patch) {
    writePlyrStorage(patch);
  }

  // --- Volume, muted and captions ---
  // Same keys and semantics as the other players sharing this storage (volume/muted/captions/language),
  // for video, premiere and live. The saved value wins over the default. The other players never force a
  // mute for autoplay, so neither is a programmatic mute persisted (see muteWithoutPersisting).

  setupPreferences() {
    const store = this.playerElement.store;
    this.volumeSnapshot = null;
    this.captionsRestored = false;
    this.captionsSnapshot = null;
    this.captionsPending = false;

    this.restoreVolume(store);
    this.unsubscribePreferences = store.subscribe(() => this.syncPreferences(store));
    this.syncPreferences(store);
  }

  restoreVolume(store) {
    const saved = this.readPlyrStorage();
    const volume = Number(saved?.volume);
    try {
      // setVolume unmutes above zero, so mute goes second.
      if (saved?.volume != null && Number.isFinite(volume) && volume >= 0 && volume <= 1) store.setVolume(volume);
      if (saved?.muted === true) store.setMuted(true);
    } catch {
      // volume is not controllable here (e.g. iOS) - keep the defaults
    }
    this.volumeSnapshot = this.volumeState(store);
  }

  volumeState(store) {
    const { volume, muted } = store.state || {};
    return typeof volume === "number" ? { volume, muted: Boolean(muted) } : null;
  }

  // For a mute that is not the viewer's choice (e.g. muted autoplay): applies it and re-baselines the
  // snapshot so the change is never written over the saved `muted`.
  muteWithoutPersisting() {
    const store = this.playerElement.store;
    store.setMuted(true);
    this.volumeSnapshot = this.volumeState(store);
  }

  syncPreferences(store) {
    this.persistVolume(store);
    this.syncCaptions(store);
  }

  persistVolume(store) {
    const current = this.volumeState(store);
    if (!current) return;
    if (!this.volumeSnapshot) {
      this.volumeSnapshot = current;
      return;
    }
    if (current.volume === this.volumeSnapshot.volume && current.muted === this.volumeSnapshot.muted) return;

    this.volumeSnapshot = current;
    this.writePlyrStorage({ volume: current.volume, muted: current.muted });
  }

  captionTracks(store) {
    return (store.state?.textTrackList || []).filter((track) => track.kind === "captions" || track.kind === "subtitles");
  }

  showingCaptionTrack(store) {
    return this.captionTracks(store).find((track) => track.mode === "showing") || null;
  }

  // Restores once, when the first caption track shows up in the (asynchronously populated) list;
  // after that, writes the viewer's changes. Captions stay off unless the saved `captions` is true.
  //
  // selectSubtitlesTrack flips the track mode synchronously but the store only reflects it on a later
  // `change` event, so after asking for the saved track nothing is written until a showing track is
  // observed (or the restore times out). The snapshot is always built from what the state shows.
  syncCaptions(store) {
    const tracks = this.captionTracks(store);

    if (!this.captionsRestored) {
      if (!tracks.length) return;

      this.captionsRestored = true;
      const saved = this.readPlyrStorage();
      if (saved?.captions === true && this.requestCaptionsTrack(store, tracks, saved)) {
        this.captionsPending = true;
        this.captionsRestoreTimer = setTimeout(() => this.finishCaptionsRestore(store), CAPTIONS_RESTORE_TIMEOUT_MS);
      } else {
        this.captionsSnapshot = this.captionsFromState(store);
      }
      return;
    }

    if (this.captionsPending) {
      if (!this.showingCaptionTrack(store)) return;
      this.finishCaptionsRestore(store);
      return;
    }

    const current = this.captionsFromState(store, this.captionsSnapshot.language);
    if (current.captions === this.captionsSnapshot.captions && current.language === this.captionsSnapshot.language) return;

    this.captionsSnapshot = current;
    this.writePlyrStorage(current.captions ? current : { captions: false });
  }

  // True when a track was selected (the state will catch up asynchronously).
  requestCaptionsTrack(store, tracks, saved) {
    const language = String(saved.language || "").toLowerCase();
    const match = tracks.find((track) => String(track.language || "").toLowerCase() === language) || tracks[0];
    try {
      store.selectSubtitlesTrack(match.id);
      return true;
    } catch {
      return false; // keep captions off
    }
  }

  // Baselines on the real state: a track that never showed leaves the snapshot at "off" without a write.
  finishCaptionsRestore(store) {
    clearTimeout(this.captionsRestoreTimer);
    this.captionsRestoreTimer = null;
    this.captionsPending = false;
    this.captionsSnapshot = this.captionsFromState(store);
  }

  captionsFromState(store, fallbackLanguage = null) {
    const showing = this.showingCaptionTrack(store);
    return showing ? { captions: true, language: showing.language } : { captions: false, language: fallbackLanguage };
  }

  // --- Progress, resume and play ping ---
  // Live and premiere have no resume or progress reporting; only live skips the play ping.

  // playbackDb only exists on the subscriber site, not on the embed.
  setupProgress() {
    if (this.isLive() || this.isPremiere()) return;
    if (!window.playbackDb) return;

    this.progressActive = true;
    // Resume first so early ticks don't report ~0s over the stored position.
    this.resumePlayback().finally(() => {
      if (!this.connected || !this.progressActive) return;
      this.media.addEventListener("timeupdate", this.boundTimeUpdate);
    });
  }

  async resumePlayback() {
    if (this.timestampValue > 0) return;

    const listenData = await window.playbackDb.getPlaybackData(this.element.getAttribute("data-item-type"), this.element.getAttribute("data-item-id"));
    if (!this.connected || !listenData) return;

    this.applyResume = () => {
      this.media.removeEventListener("loadedmetadata", this.applyResume);
      // A timestamp set while waiting (seekTo) still wins.
      if (this.timestampValue > 0) return;
      this.seekThroughStore(listenData.currentTime);
    };
    if (this.media.readyState >= 1) {
      this.applyResume();
    } else {
      this.media.addEventListener("loadedmetadata", this.applyResume);
    }
  }

  // The session is shared with the persistent mini audio player, so the video only
  // takes it over once it actually plays.
  setupMediaSession() {
    if (!("mediaSession" in navigator)) return;

    this.mediaSessionActive = true;
    this.boundMediaSessionPlay = () => {
      this.claimMediaSession();
      this.updateMediaSessionPlaybackState();
    };
    this.boundMediaSessionState = () => this.updateMediaSessionPlaybackState();
    this.media.addEventListener("play", this.boundMediaSessionPlay);
    MEDIA_SESSION_EVENTS.forEach((name) => this.media.addEventListener(name, this.boundMediaSessionState));
    if (!this.media.paused) this.boundMediaSessionPlay();
  }

  claimMediaSession() {
    const store = this.playerElement.store;
    const session = navigator.mediaSession;
    claimMediaSession(this);

    if (this.videoTitleValue && typeof MediaMetadata === "function") {
      session.metadata = new MediaMetadata({ title: this.videoTitleValue });
    }

    const handlers = {
      play: () => store.play(),
      pause: () => store.pause(),
      seekbackward: (details) => this.seekBy(-(details?.seekOffset || 10)),
      seekforward: (details) => this.seekBy(details?.seekOffset || 10)
    };
    Object.entries(handlers).forEach(([action, handler]) => {
      try { session.setActionHandler(action, handler); } catch { /* action unsupported */ }
    });
  }

  teardownMediaSession() {
    if (!this.mediaSessionActive) return;
    this.mediaSessionActive = false;

    this.media?.removeEventListener("play", this.boundMediaSessionPlay);
    MEDIA_SESSION_EVENTS.forEach((name) => this.media?.removeEventListener(name, this.boundMediaSessionState));
    if (!releaseMediaSession(this)) return;
    ["play", "pause", "seekbackward", "seekforward"].forEach((action) => {
      try { navigator.mediaSession.setActionHandler(action, null); } catch { /* ignore */ }
    });
    navigator.mediaSession.metadata = null;
    navigator.mediaSession.playbackState = "none";
  }

  updateMediaSessionPlaybackState() {
    if (!this.media || !ownsMediaSession(this)) return;
    const session = navigator.mediaSession;
    const { duration, currentTime, playbackRate, paused } = this.media;

    // setPositionState throws on a non-finite duration (live) or a position past the duration.
    if (Number.isFinite(duration) && duration > 0) {
      try {
        session.setPositionState({
          duration,
          playbackRate: playbackRate || 1,
          position: Math.min(Math.max(currentTime || 0, 0), duration)
        });
      } catch { /* invalid position state */ }
    }
    session.playbackState = paused ? "paused" : "playing";
  }

  seekBy(offset) {
    const { currentTime = 0, duration } = this.media;
    let target = Math.max(currentTime + offset, 0);
    if (Number.isFinite(duration)) target = Math.min(target, duration);
    this.seekThroughStore(target);
  }

  setupWakeLock() {
    if (!("wakeLock" in navigator)) return;

    this.wakeLockActive = true;
    this.boundWakePlay = () => {
      this.acquireWakeLock();
      document.addEventListener("visibilitychange", this.boundWakeVisibility);
    };
    this.boundWakePause = () => this.releaseWakeLock();
    this.boundWakeEnded = () => {
      this.releaseWakeLock();
      document.removeEventListener("visibilitychange", this.boundWakeVisibility);
    };
    this.boundWakeVisibility = () => {
      if (document.visibilityState === "visible" && !this.media?.paused) {
        this.acquireWakeLock();
      } else {
        this.releaseWakeLock();
      }
    };
    this.media.addEventListener("play", this.boundWakePlay);
    this.media.addEventListener("pause", this.boundWakePause);
    this.media.addEventListener("ended", this.boundWakeEnded);
    if (!this.media.paused) this.boundWakePlay();
  }

  teardownWakeLock() {
    if (!this.wakeLockActive) return;
    this.wakeLockActive = false;

    this.media?.removeEventListener("play", this.boundWakePlay);
    this.media?.removeEventListener("pause", this.boundWakePause);
    this.media?.removeEventListener("ended", this.boundWakeEnded);
    document.removeEventListener("visibilitychange", this.boundWakeVisibility);
    this.releaseWakeLock();
  }

  async acquireWakeLock() {
    if (this.wakeLock || this.wakeLockPending || !this.wakeLockActive || this.isCasting) return;

    this.wakeLockPending = true;
    try {
      const lock = await navigator.wakeLock.request("screen");
      // The request can resolve after a pause or disconnect; don't keep the screen awake then.
      if (!this.wakeLockActive || this.media?.paused) {
        lock.release().catch(() => {});
        return;
      }
      this.wakeLock = lock;
      lock.addEventListener("release", () => {
        if (this.wakeLock === lock) this.wakeLock = null;
      });
    } catch {
      // Denied (e.g. low battery or hidden tab); playback is unaffected.
    } finally {
      this.wakeLockPending = false;
    }
  }

  async releaseWakeLock() {
    const lock = this.wakeLock;
    this.wakeLock = null;
    try { await lock?.release(); } catch { /* already released */ }
  }

  // iPhone hands fullscreen to the native player, where the lock may not apply; that is fine.
  setupOrientationLock() {
    if (!screen.orientation) return;

    const store = this.playerElement.store;
    this.orientationFullscreen = !!store.state?.isFullscreen;
    this.unsubscribeOrientation = store.subscribe(() => {
      const fullscreen = !!store.state?.isFullscreen;
      if (fullscreen === this.orientationFullscreen) return;
      this.orientationFullscreen = fullscreen;
      this.applyOrientationLock(fullscreen);
    });
  }

  teardownOrientationLock() {
    this.unsubscribeOrientation?.();
    this.unsubscribeOrientation = null;
    if (this.orientationFullscreen) this.applyOrientationLock(false);
    this.orientationFullscreen = false;
  }

  // iPhone Safari pauses the video when the native player closes (Done or swipe-down). Resume it
  // unless the viewer paused it themselves.
  setupFullscreenResume() {
    const video = this.nativeVideo;
    if (!video || typeof video.webkitSetPresentationMode !== "function" || this.fullscreenResumeActive) return;

    this.fullscreenResumeActive = true;
    this.fullscreenResumeVideo = video;
    this.nativeFullscreen = video.webkitPresentationMode === "fullscreen";
    this.lastFullscreenPauseAt = 0;
    this.boundFullscreenMode = () => {
      if (video.webkitPresentationMode === "fullscreen") this.nativeFullscreen = true;
      else this.handleFullscreenExit();
    };
    this.boundFullscreenEnd = () => this.handleFullscreenExit();
    this.boundFullscreenPause = () => {
      if (this.nativeFullscreen) this.lastFullscreenPauseAt = Date.now();
    };
    video.addEventListener("webkitpresentationmodechanged", this.boundFullscreenMode);
    video.addEventListener("webkitendfullscreen", this.boundFullscreenEnd);
    this.media.addEventListener("pause", this.boundFullscreenPause);
  }

  teardownFullscreenResume() {
    if (!this.fullscreenResumeActive) return;

    this.fullscreenResumeVideo.removeEventListener("webkitpresentationmodechanged", this.boundFullscreenMode);
    this.fullscreenResumeVideo.removeEventListener("webkitendfullscreen", this.boundFullscreenEnd);
    this.media?.removeEventListener("pause", this.boundFullscreenPause);
    this.disarmFullscreenResume();
    this.fullscreenResumeActive = false;
    this.fullscreenResumeVideo = null;
    this.nativeFullscreen = false;
  }

  disarmFullscreenResume() {
    this.closeFullscreenResumeWindow();
    clearTimeout(this.fullscreenRetryTimer);
    this.fullscreenRetryTimer = null;
  }

  // Window expiry: stop listening for late pauses but let an already-scheduled retry run.
  closeFullscreenResumeWindow() {
    clearTimeout(this.fullscreenResumeTimer);
    this.fullscreenResumeTimer = null;
    if (this.boundFullscreenLatePause) this.media?.removeEventListener("pause", this.boundFullscreenLatePause);
    this.boundFullscreenLatePause = null;
  }

  // Both exit signals land here; only the first one while in fullscreen counts. Done (X) and
  // swipe-down differ in when WebKit pauses relative to the mode event, and WebKit can pause again
  // right after a play() issued mid-transition, so: resume now if the pause already happened, and
  // stay armed for the window, re-resuming (a few attempts) on every pause that lands in it.
  handleFullscreenExit() {
    if (!this.nativeFullscreen) return;

    this.nativeFullscreen = false;
    const media = this.media;
    if (!media || media.ended) return;

    const recentPause = Date.now() - this.lastFullscreenPauseAt <= FULLSCREEN_EXIT_PAUSE_WINDOW_MS;
    // Paused with no recent pause means the viewer paused inside the native player: respect it.
    if (media.paused && !recentPause) return;

    this.disarmFullscreenResume();
    this.fullscreenResumeAttempts = 0;
    this.boundFullscreenLatePause = () => this.scheduleFullscreenResume();
    media.addEventListener("pause", this.boundFullscreenLatePause);
    this.fullscreenResumeTimer = setTimeout(() => this.closeFullscreenResumeWindow(), FULLSCREEN_EXIT_PAUSE_WINDOW_MS);
    if (media.paused) this.scheduleFullscreenResume(0);
  }

  // Deferred so play() lands after WebKit's own exit-time pause rather than before it.
  scheduleFullscreenResume(delay = 100) {
    if (this.fullscreenResumeAttempts >= FULLSCREEN_RESUME_MAX_ATTEMPTS) return;

    clearTimeout(this.fullscreenRetryTimer);
    this.fullscreenRetryTimer = setTimeout(() => {
      this.fullscreenRetryTimer = null;
      const media = this.media;
      if (!media || media.ended || !media.paused) return;

      this.fullscreenResumeAttempts += 1;
      this.playVideo();
    }, delay);
  }

  applyOrientationLock(locked) {
    try {
      if (locked) {
        Promise.resolve(screen.orientation.lock("landscape")).catch(() => {});
      } else {
        screen.orientation.unlock();
      }
    } catch { /* unsupported or not allowed */ }
  }

  seekThroughStore(seconds) {
    const store = this.playerElement?.store;
    if (typeof store?.seek === "function") {
      Promise.resolve(store.seek(seconds)).catch(() => {});
    } else {
      this.media.currentTime = seconds;
    }
  }

  updateTime() {
    // Once the receiver has the media the local player is paused and silenced; the receiver reports
    // progress instead. Before that (picker, load) the local position is still the real one.
    if (this.isCasting && this.castLoaded) return;

    this.reportPlaybackProgress({
      currentTime: this.media.currentTime,
      duration: Number.isFinite(this.media.duration) ? this.media.duration : 0,
      isPlaying: !this.media.paused
    });
  }

  // Seconds, like every other SupercastTimeUpdateEvent source (local player, native bridge).
  reportPlaybackProgress({ currentTime, duration, isPlaying }) {
    window.dispatchEvent(new SupercastTimeUpdateEvent({
      itemId: this.element.getAttribute("data-item-id"),
      itemType: this.element.getAttribute("data-item-type"),
      currentTime,
      duration,
      isPlaying,
      mediaType: "video"
    }));
  }

  // Once per player: canplay fires again after seeks and rebuffers. Skipped on the embed (no playbackDb).
  updateFeedTokenConnection() {
    if (!window.playbackDb) return;
    if (this.pinged) return;
    if (this.isLive()) return;

    const episodeId = this.element.getAttribute("data-item-id");
    if (!episodeId) return;

    this.pinged = true;
    fetch(`/subscriber_v2/feed_items/mark_feed_token_connected?episode_id=${episodeId}`, { method: "POST" }).catch(() => {});
  }

  hlsUrl() {
    return this.isLive() ? this.liveUrlValue : this.element.getAttribute("data-playlist-url");
  }

  // Idempotent: connect() and the end of preshow can both ask for playback.
  startPlayback() {
    if (this.sourceLoaded) return;
    this.sourceLoaded = true;
    this.loadSource();
  }

  // The console refresh on go-live flips preshow to false; liveUrlValue is already the fresh one.
  // Stimulus also calls this on initialize (previous undefined), which is ignored.
  async preshowValueChanged(value, previous) {
    if (previous !== true || value !== false) return;
    if (!this.connected) return;
    if (!(await this.whenPlayerReady())) return;
    this.startPlayback();
  }

  // Video.js plays the HLS source through <hlsjs-video> (hls.js, or native HLS on WebKit). The
  // engine owns recoverable network/media errors; this controller only acts on a terminal one (see
  // handleMediaError) by loading a different source - never by retrying the same one.
  async loadSource() {
    let src = this.hlsUrl();
    if (!src) return;

    // The autoplay attribute is the v10 mechanism; the browser may still block it.
    this.media.autoplay = this.autoplayValue;

    if (this.timestampValue > 0) {
      this.applyTimestamp = () => {
        this.media.removeEventListener("loadedmetadata", this.applyTimestamp);
        this.media.currentTime = this.timestampValue;
      };
      this.media.addEventListener("loadedmetadata", this.applyTimestamp);
    }

    // Live has no original-URL fallback, so its signed manifest is loaded directly.
    if (!this.isLive()) {
      const playlist = await this.resolvePlaylist(src);
      if (!this.connected) return;
      if (playlist.reason) {
        this.loadOriginalUrl(playlist.reason, playlist.params);
        return;
      }
      src = playlist.src;
    }

    this.markSourceLoading();
    this.media.source = this.hlsSource(src);
    this.startMuxMonitoring();
    if (this.awsHlsValue && !this.isLive()) this.startManifestPolling(src);
  }

  hlsSource(src) {
    return {
      src,
      // crossorigin on the element already sends the CloudFront cookies (use-credentials), so no xhrSetup.
      // Every WebKit browser keeps native HLS (needed for AirPlay); everyone else uses hls.js.
      ...(window.WebKitPlaybackTargetAvailabilityEvent ? { preferPlayback: "native" } : {}),
      engine: {
        hlsJs: {
          // Video.js defaults to 30.
          backBufferLength: 90,
          ...(this.isLive() ? { lowLatencyMode: true } : {}),
          // AWS manifests may advertise a subtitle rendition (for native apps/AirPlay/Cast); the web
          // sideloads its own <track>, so hls.js ignores the manifest WebVTT.
          ...(this.awsHlsValue ? { enableWebVTT: false } : {})
        }
      }
    };
  }

  // A missing playlist (not transcoded yet, or a cross-origin CORS/network failure on a premiere's
  // CDN URL) would only surface later as a terminal error, so check up front and go straight to the MP4.
  // Bunny: the route only redirects to the signed URL after a session check, which an AirPlay receiver
  // can't pass, so the GET also resolves the final URL (falling back to the route if it can't).
  async resolvePlaylist(src) {
    const aws = this.awsHlsValue;
    if (aws && !this.originalUrl) return { src };

    try {
      const response = await fetch(src, aws ? { method: "HEAD", credentials: "include" } : { credentials: "same-origin" });
      response.body?.cancel();
      if (response.ok) return { src: aws ? src : response.url || src };
      if (!this.originalUrl) return { src };
      return { src, reason: "playlist_head_not_ok", params: { http_status: response.status } };
    } catch {
      if (!this.originalUrl) return { src };
      return { src, reason: "playlist_head_threw", params: {} };
    }
  }

  get originalUrl() {
    return this.element.getAttribute("data-original-url");
  }

  // Terminal failure from the media element, after hls.js (or the native engine) has exhausted its
  // own recovery. Recovery is a different source: the MP4 for VOD/premiere, a fresh page (fresh signed
  // manifest) for live.
  handleMediaError() {
    const error = this.media?.error;
    if (!error || error.code === MEDIA_ERR_ABORTED) return;
    if (this.preshowValue) return;

    this.addPlaybackBreadcrumb("media_error", { code: error.code, message: error.message });

    if (this.isLive()) {
      this.reportPlaybackError("Video player exhausted playback recovery", { media_error_code: error.code });
      this.reloadOnceForLive();
      return;
    }

    // Already on the MP4 (or nothing to fall back to): a second failure is final, never a loop.
    if (this.fellBack || !this.originalUrl) {
      this.reportPlaybackError("Video player failed with no fallback left", { media_error_code: error.code });
      return;
    }

    this.reportPlaybackError("Video player exhausted playback recovery", { media_error_code: error.code });
    this.loadOriginalUrl("fatal_media_error");
  }

  // Swap to the progressive MP4, keeping the playhead. Reported first: the live branch can unload the page.
  loadOriginalUrl(reason, params = {}) {
    this.reportPlaybackError("Video player fell back to the original (non-HLS) URL", {
      fallback_reason: reason,
      ...params
    });

    if (!this.originalUrl) return;

    clearTimeout(this.manifestPollTimer);
    this.fellBack = true;

    const resumePosition = this.ready ? this.media.currentTime : 0;
    if (resumePosition > 0) {
      this.media.addEventListener("loadedmetadata", () => {
        this.media.currentTime = resumePosition;
      }, { once: true });
    }

    this.media.autoplay = this.autoplayValue;
    this.markSourceLoading();
    // The hls.js engine goes away with this source change: unbind Mux from it first, so the swap is
    // a new view (videochange) rather than a failure of the old one.
    this.muxRemoveHls();
    this.media.source = { src: this.originalUrl, type: "video/mp4" };
    if (this.muxAttached) {
      this.muxSourceChange(muxBranchProgressive);
    } else {
      this.startMuxMonitoring(muxBranchProgressive);
    }
  }

  // Keyed per item (data-item-id), so one broken item gets at most one
  // reload per session and a stale marker from another item can't suppress recovery.
  reloadGuardKey() {
    return `supercast-video-player-reloaded-after-error:${this.element.getAttribute("data-item-id")}`;
  }

  // Live's only recovery: a fresh page mints a fresh signed manifest. The guard is claimed before
  // the (possibly deferred) reload so a second failure inside that window cannot start another.
  reloadOnceForLive() {
    if (window.sessionStorage.getItem(this.reloadGuardKey())) return;

    window.sessionStorage.setItem(this.reloadGuardKey(), "1");

    const pending = this.reportPlaybackError("Video player forced a page reload to recover live playback", {
      recovery: "page_reload"
    });

    if (!pending) {
      window.location.reload();
      return;
    }

    // The reload destroys the page before an in-flight report settles, so give it a short head
    // start, capped so a hung request can't leave a broken player on screen.
    Promise.race([pending, new Promise((resolve) => setTimeout(resolve, 500))])
      .catch(() => {})
      .then(() => window.location.reload());
  }

  // Playback actually started: the reload marker has done its job.
  handleCanPlay() {
    this.updateFeedTokenConnection();
    this.premiereCanPlay();
    window.sessionStorage.removeItem(this.reloadGuardKey());
  }

  // Polls the master manifest for newly finished rungs (AWS HLS VOD/premiere on the hls.js engine).
  // media.engine is the Hls instance; it is undefined on the native-HLS path, where there is nothing
  // to reload through, so polling is skipped there (checked before any fetch).
  async startManifestPolling(src) {
    if (!this.media?.engine) return;

    this.manifestPollAttempts = 0;
    this.manifestRungCount = await this.fetchManifestRungCount(src);
    if (this.manifestRungCount === null) return; // no baseline - don't guess, don't poll

    this.scheduleManifestPoll(src);
  }

  scheduleManifestPoll(src) {
    if (!this.connected || this.fellBack) return;
    if (this.manifestRungCount >= MASTER_MANIFEST_RUNG_TOTAL) return;
    if (this.manifestPollAttempts >= MASTER_MANIFEST_MAX_POLL_ATTEMPTS) return;

    this.manifestPollTimer = setTimeout(() => this.pollManifest(src), MASTER_MANIFEST_POLL_INTERVAL_MS);
  }

  async pollManifest(src) {
    this.manifestPollAttempts += 1;

    const count = await this.fetchManifestRungCount(src);
    if (!this.connected || this.fellBack) return;

    if (count !== null && count > this.manifestRungCount) {
      this.manifestRungCount = count;
      this.reloadManifest(src);
    }

    this.scheduleManifestPoll(src);
  }

  // Counting #EXT-X-STREAM-INF lines is enough to detect growth. Never throws: a failed fetch
  // just means "try again next tick".
  async fetchManifestRungCount(src) {
    try {
      const response = await fetch(src, { credentials: this.awsHlsValue ? "include" : "same-origin", cache: "no-store" });
      if (!response.ok) return null;

      const body = await response.text();
      return (body.match(/#EXT-X-STREAM-INF/g) || []).length;
    } catch {
      return null;
    }
  }

  // Re-reads the (now richer) master on the live hls.js instance. The media stays attached, so
  // play/pause state is kept; startPosition makes hls.js resume where playback is rather than at 0.
  reloadManifest(src) {
    const engine = this.media?.engine;
    if (!engine) return;

    engine.config.startPosition = this.media.currentTime;
    // Go through the adapter's src setter, not engine.loadSource: the setter wraps the load in
    // withPreservedTextTracks, which restores the caption cues hls.js clears on a reload.
    this.media.src = src;
  }

  tokenExpiryTags() {
    if (!this.hasExpiresAtMsValue || !this.expiresAtMsValue) return { token_expiry: "unknown" };

    const remainingSeconds = Math.round((this.expiresAtMsValue - Date.now()) / 1000);
    return {
      token_expiry: remainingSeconds <= 0 ? "expired" : "valid",
      token_expires_in_s: remainingSeconds
    };
  }

  playbackTags() {
    return {
      cdn: this.cdnValue,
      stream_type: this.streamTypeValue,
      playback_kind: this.isLive() ? "live" : this.isPremiere() ? "premiere" : "vod",
      token_type: this.tokenTypeValue,
      client_surface: "web",
      plan_tier: this.planTierValue,
      sub_property_id: this.subPropertyIdValue,
      viewer_id: this.viewerIdValue,
      item_id: this.element.getAttribute("data-item-id"),
      item_type: this.element.getAttribute("data-item-type"),
      ...this.tokenExpiryTags()
    };
  }

  // Breadcrumbs ride along on the next AppSignal report: the "how did we get here" trail.
  addPlaybackBreadcrumb(action, metadata = {}) {
    if (!this.observabilityValue) return;

    sendPlaybackBreadcrumb("video_player", action, { ...metadata, item_id: this.element.getAttribute("data-item-id") });
  }

  // Returns the send Promise, or undefined when observability is off / the SDK is unavailable.
  reportPlaybackError(message, tags = {}) {
    if (!this.observabilityValue) return undefined;

    return sendPlaybackError(message, {
      action: "media--video-player",
      tags: { ...this.playbackTags(), ...tags }
    });
  }

  // --- Live ---

  // Playing, including resuming from pause, snaps to the live edge when behind.
  setupLive() {
    if (!this.isLive()) return;

    this.boundSeekToLiveEdge = () => this.seekToLiveEdge();
    this.media.addEventListener("play", this.boundSeekToLiveEdge);
  }

  teardownLive() {
    if (this.boundSeekToLiveEdge) this.media?.removeEventListener("play", this.boundSeekToLiveEdge);
    this.boundSeekToLiveEdge = null;
  }

  liveEdgePosition() {
    const sync = this.media.engine?.liveSyncPosition;
    if (Number.isFinite(sync)) return sync;
    const seekable = this.media.seekable;
    return seekable && seekable.length > 0 ? seekable.end(seekable.length - 1) : null;
  }

  seekToLiveEdge() {
    if (this.isCasting) return;
    const edge = this.liveEdgePosition();
    if (edge != null && edge - this.media.currentTime > LIVE_EDGE_TOLERANCE_SECONDS) this.media.currentTime = edge;
  }

  // --- Premiere ---

  // Everyone shares a server-clock playhead and cannot seek past it.
  setupPremiere() {
    if (!this.isPremiere()) return;

    this.boundClampToPremiereEdge = () => this.clampToPremiereEdge();
    this.media.addEventListener("timeupdate", this.boundClampToPremiereEdge);
    this.media.addEventListener("seeking", this.boundClampToPremiereEdge);
    this.boundHoldPremiere = () => this.holdPremiere();
    this.media.addEventListener("play", this.boundHoldPremiere);
    this.syncPremiereHold();
    this.updatePremiereEdgeState();
    this.setupPremiereTip();
    this.premiereEdgeInterval = setInterval(() => {
      this.syncPremiereHold();
      this.updatePremiereEdgeState();
      this.syncPremiereTip();
    }, 1000);
  }

  // Before T-0 the playhead is still 0, so playing would loop against the edge clamp.
  premiereHeld() {
    return this.isPremiere() && this.heldValue && !this.premiereStarted && this.serverNow() < this.publishedAtMsValue;
  }

  // Controls are hidden while held; the wrapper is inert so they would be dead.
  syncPremiereHold() {
    const held = this.premiereHeld();
    this.element.querySelectorAll("media-controls").forEach((controls) => controls.toggleAttribute("hidden", held));
    if (held) this.media.autoplay = false;
  }

  // Catches every way to start playback.
  holdPremiere() {
    if (this.premiereHeld()) this.media.pause();
  }

  teardownPremiere() {
    clearInterval(this.premiereEdgeInterval);
    this.premiereEdgeInterval = null;
    this.teardownPremiereTip();
    if (!this.boundClampToPremiereEdge) return;

    this.media?.removeEventListener("timeupdate", this.boundClampToPremiereEdge);
    this.media?.removeEventListener("seeking", this.boundClampToPremiereEdge);
    this.media?.removeEventListener("play", this.boundHoldPremiere);
    this.boundClampToPremiereEdge = null;
  }

  serverNow() {
    return Date.now() + (this.clockOffsetMs || 0);
  }

  // Seconds since the premiere started, capped at the duration.
  premiereLivePos() {
    return Math.min(Math.max((this.serverNow() - this.publishedAtMsValue) / 1000, 0), this.durationValue);
  }

  // Only forward seeks past the edge are clamped; after the premiere ends scrubbing is free.
  clampToPremiereEdge() {
    if (this.isCasting) return;
    const livePos = this.premiereLivePos();
    if (livePos >= this.durationValue) return;

    if (this.media.currentTime > livePos + PREMIERE_EDGE_TOLERANCE_SECONDS) this.media.currentTime = livePos;
  }

  // Disable forward seek while caught up so it is not a silent no-op.
  updatePremiereEdgeState() {
    const atEdge = this.premiereLivePos() - this.media.currentTime <= PREMIERE_EDGE_TOLERANCE_SECONDS;
    this.element.querySelectorAll("media-seek-button").forEach((button) => {
      if (Number(button.getAttribute("seconds")) > 0) button.toggleAttribute("disabled", atEdge);
    });
  }

  // First canplay of a premiere: land on the shared playhead (never the resume position).
  premiereCanPlay() {
    if (!this.isPremiere() || this.premiereLoaded) return;

    this.premiereLoaded = true;
    this.media.currentTime = this.premiereLivePos();
    if (this.autoplayValue) this.playVideo();
  }

  // T-0 signal from a premiere mounted early; if not loaded yet, premiereCanPlay seeks and plays.
  startPremiere() {
    if (!this.media) return;

    this.premiereStarted = true;
    this.syncPremiereHold();
    this.syncPremiereTip();
    this.autoplayValue = true;
    this.media.autoplay = true;
    if (!this.premiereLoaded) return;

    this.media.currentTime = this.premiereLivePos();
    this.playVideo();
  }

  // --- Premiere tip ---
  // The tip is the shared playhead. From T-0 until it reaches the end (and not while casting) the
  // slider marks it with a tick and hatches what has not aired yet, and every seek is capped to it.

  premiereTipActive() {
    return this.isPremiere() && !this.isCasting && this.serverNow() >= this.publishedAtMsValue &&
      this.premiereLivePos() < this.durationValue;
  }

  setupPremiereTip() {
    this.installPremiereSeekCap();
    // The skin renders exactly one time slider.
    const slider = this.element.querySelector("media-time-slider");
    if (!slider) return;

    this.premiereTipSlider = slider;
    this.boundPremiereTipPointerMove = (event) => {
      this.premiereTipPointerX = event.clientX;
      this.syncPremiereTipPointer();
    };
    this.boundPremiereTipPointerLeave = () => {
      this.premiereTipPointerX = null;
      this.syncPremiereTipPointer();
    };
    slider.addEventListener("pointermove", this.boundPremiereTipPointerMove);
    slider.addEventListener("pointerleave", this.boundPremiereTipPointerLeave);
    slider.addEventListener("pointercancel", this.boundPremiereTipPointerLeave);
    this.syncPremiereTip();
  }

  teardownPremiereTip() {
    this.uninstallPremiereSeekCap();
    const slider = this.premiereTipSlider;
    if (!slider) return;

    this.clearPremiereTip();
    slider.removeEventListener("pointermove", this.boundPremiereTipPointerMove);
    slider.removeEventListener("pointerleave", this.boundPremiereTipPointerLeave);
    slider.removeEventListener("pointercancel", this.boundPremiereTipPointerLeave);
    this.premiereTipSlider = null;
    this.premiereTipPointerX = null;
  }

  // Called every second, at T-0 and on cast start/stop.
  syncPremiereTip() {
    const slider = this.premiereTipSlider;
    if (!slider) return;
    if (!this.premiereTipActive()) {
      this.clearPremiereTip();
      return;
    }

    const livePos = this.premiereLivePos();
    // The slider's own scale: Video.js computes the fill from the media duration.
    const mediaDuration = this.media?.duration;
    const duration = Number.isFinite(mediaDuration) && mediaDuration > 0 ? mediaDuration : this.durationValue;
    this.premiereTipRawPct = Math.min((livePos / duration) * 100, 100);
    slider.style.setProperty("--premiere-tip", `${this.edgeAlignedPercent(slider, this.premiereTipRawPct).toFixed(3)}%`);
    slider.toggleAttribute("data-premiere-tip-active", true);

    const description = this.element.querySelector("[data-premiere-tip-description]");
    const thumb = slider.querySelector("media-slider-thumb");
    if (description) {
      description.textContent = `Premiere is at ${formatClock(livePos)}`;
      thumb?.setAttribute("aria-describedby", description.id);
    }
    this.syncPremiereTipPointer();
  }

  clearPremiereTip() {
    const slider = this.premiereTipSlider;
    if (!slider) return;

    this.premiereTipRawPct = null;
    slider.style.removeProperty("--premiere-tip");
    slider.removeAttribute("data-premiere-tip-active");
    slider.removeAttribute("data-past-tip");
    slider.querySelector("media-slider-thumb")?.removeAttribute("aria-describedby");
    const description = this.element.querySelector("[data-premiere-tip-description]");
    if (description) description.textContent = "";
  }

  // With thumb-alignment="edge" Video.js maps 0-100% onto [half thumb, 100% - half thumb] for its
  // fill and pointer vars; the tip is drawn in that same space so the thumb stops on the tick.
  edgeAlignedPercent(slider, rawPercent) {
    const thumb = slider.querySelector("media-slider-thumb");
    const trackSize = slider.offsetWidth;
    if (slider.getAttribute("thumb-alignment") !== "edge" || !thumb || trackSize === 0) return rawPercent;

    const thumbHalf = (thumb.offsetWidth / trackSize) * 50;
    return thumbHalf + (rawPercent / 100) * (100 - thumbHalf * 2);
  }

  // The hover preview reads "Not aired yet" past the tip. Compared in value space (the raw pointer
  // percent, as Video.js computes the preview time), so it flips exactly where the previewed time
  // passes the tip.
  syncPremiereTipPointer() {
    const slider = this.premiereTipSlider;
    if (!slider) return;

    let pastTip = false;
    if (this.premiereTipPointerX != null && this.premiereTipRawPct != null) {
      const rect = slider.getBoundingClientRect();
      const pointerPct = rect.width > 0 ? ((this.premiereTipPointerX - rect.left) / rect.width) * 100 : 0;
      pastTip = pointerPct > this.premiereTipRawPct;
    }
    slider.toggleAttribute("data-past-tip", pastTip);
  }

  // Every seek path (slider, hotkeys, seek buttons, store.seek, our own code) ends in a write to the
  // media element's currentTime, so capping that setter keeps playback from ever landing past the
  // tip. hls.js seeks the inner <video> directly, which correctly bypasses it. clampToPremiereEdge
  // stays as a backstop.
  installPremiereSeekCap() {
    const media = this.media;
    if (!media || this.premiereSeekCapMedia) return;

    const descriptor = findAccessorDescriptor(media, "currentTime");
    if (!descriptor) return;

    const controller = this;
    this.premiereSeekCapOwnDescriptor = Object.getOwnPropertyDescriptor(media, "currentTime");
    Object.defineProperty(media, "currentTime", {
      configurable: true,
      enumerable: descriptor.enumerable,
      get() {
        return descriptor.get.call(this);
      },
      set(value) {
        descriptor.set.call(this, controller.capPremiereSeek(value));
      }
    });
    this.premiereSeekCapMedia = media;
  }

  uninstallPremiereSeekCap() {
    const media = this.premiereSeekCapMedia;
    if (!media) return;

    delete media.currentTime;
    if (this.premiereSeekCapOwnDescriptor) Object.defineProperty(media, "currentTime", this.premiereSeekCapOwnDescriptor);
    this.premiereSeekCapMedia = null;
    this.premiereSeekCapOwnDescriptor = null;
  }

  capPremiereSeek(value) {
    if (!this.premiereTipActive() || !Number.isFinite(value)) return value;
    return Math.min(value, this.premiereLivePos());
  }

  // --- Cast ---

  castDuration() {
    const remote = this.remotePlayer?.duration;
    return remote > 0 ? remote : this.media.duration;
  }

  clearCastReportTimer() {
    clearTimeout(this.castReportTimer);
    this.castReportTimer = null;
  }

  // Receiver progress while casting. Throttled reports (time ticks) go out at most once a second,
  // with one trailing report for a dropped tick; pause/resume reports are never throttled.
  reportRemoteProgress({ isPlaying, throttle = false } = {}) {
    const p = this.remotePlayer;
    if (this.isLive() || !(this.isCasting && this.castLoaded && p.isConnected && p.isMediaLoaded && p.currentTime > 0)) return;
    if (throttle) {
      const wait = 1000 - (Date.now() - (this.castLastTimeReportAt || 0));
      if (wait > 0) {
        this.castReportTimer ||= setTimeout(() => {
          this.castReportTimer = null;
          this.reportRemoteProgress({ throttle: true });
        }, wait);
        return;
      }
      this.castLastTimeReportAt = Date.now();
    }
    this.reportPlaybackProgress({ currentTime: p.currentTime, duration: this.castDuration(), isPlaying: isPlaying ?? !p.isPaused });
  }

  // Final save for a cast that ends or is left, so the stored position never depends on a local seek.
  reportCastFinalProgress() {
    if (!this.castLoaded || this.castFinishedReported || this.isLive() || !(this.castLastTime > 0)) return;
    this.reportPlaybackProgress({ currentTime: this.castLastTime, duration: this.castDuration(), isPlaying: false });
  }

  // The shared premiere playhead when available, else the element's own position.
  castPremierePosition() {
    return typeof this.premiereLivePos === "function" ? this.premiereLivePos() : this.media.currentTime;
  }

  // Cast mutes the player without the viewer choosing it, so don't save that as a volume preference.
  setMutedWithoutPersisting(muted) {
    const store = this.playerElement?.store;
    try {
      store.setMuted(muted);
    } catch {
      this.media.muted = muted;
    }
    this.volumeSnapshot = this.volumeState(store);
  }

  pauseThroughStore() {
    const store = this.playerElement?.store;
    if (typeof store?.pause === "function") {
      Promise.resolve(store.pause()).catch(() => {});
    }
    this.media.pause();
  }

  teardownCast() {
    this.stopCastPolling();
    this.bumpCastAttempt();
    if (this.isCasting) this.reportCastFinalProgress();
    if (this.isCasting && this.castPreviousMuted !== undefined) this.setMutedWithoutPersisting(this.castPreviousMuted);
    this.teardownCastTracking();
    this.teardownAirplayCastUrl();
  }

  async toggleCast() {
    if (this.isCasting) {
      this.stopCasting();
    } else {
      this.cast();
    }
  }

  bumpCastAttempt() {
    this.castAttempt = (this.castAttempt || 0) + 1;
    return this.castAttempt;
  }

  // Mirrors isCasting onto the cast buttons so the skin can swap the enter/exit icon.
  syncCastButtons() {
    const state = this.isCasting ? "connected" : "disconnected";
    this.castButtonTargets.forEach((button) => { button.dataset.castState = state; });
    // Both cast transitions pass through here: the premiere tip hides while casting and comes back after.
    this.syncPremiereTip();
  }

  async cast() {
    const attempt = this.bumpCastAttempt();
    const stale = () => attempt !== this.castAttempt;

    // Silence the browser player BEFORE the picker and the fetch below, which can take seconds.
    this.castLocalWasPlaying = !this.media.paused;
    this.isCasting = true;
    this.syncCastButtons();
    const localPosition = this.localCastStartPosition();
    this.silenceLocalPlayback();
    this.castLastTime = 0;
    this.castRemotePaused = false;
    this.castLoaded = false;
    this.castFinishedReported = false;

    let hadSession = false;
    let stage = "session";
    try {
      hadSession = !!this.castContext?.getCurrentSession?.();
      const session = await this.getChromeCastSession(true);
      if (stale()) {
        // A late device pick must not leave a receiver app open on the TV with no media, unless a
        // newer attempt is now active and may be using this very session.
        if (session && !hadSession && !this.isCasting) session.endSession(true);
        return;
      }
      if (!session) throw new Error("No cast session");
      // AWS HLS: the receiver holds none of this browser's cookies, so the sender fetches the feed item
      // JSON with its own session and hands over only the cookie-free cast_url it carries. Everything
      // else - and any failure here - casts the MP4 redirect as before.
      stage = "payload";
      const castUrl = await this.fetchCastUrl();
      if (stale()) return;
      const url = castUrl || this.originalUrl;
      if (!url) throw new Error("no cast url");
      stage = "load";
      const contentType = castUrl ? this.castContentTypeValue : this.fileTypeValue;
      const buildRequest = (withTracks) => {
        const mediaInfo = new chrome.cast.media.MediaInfo(url, contentType);
        const request = new chrome.cast.media.LoadRequest(mediaInfo);
        request.autoplay = true;
        if (withTracks) this.attachCastCaptions(mediaInfo, request);
        if (localPosition > 0) request.currentTime = localPosition;
        return request;
      };
      const request = buildRequest(true);
      const hasTracks = !!request.media.tracks?.length;
      // Resolves null on success, otherwise a short failure reason (the framework error code string,
      // a rejection's code/message, or a synchronous throw's message).
      const load = (req) => {
        let loading;
        try {
          loading = Promise.resolve(session.loadMedia(req));
        } catch (error) {
          return Promise.resolve(castFailureReason(error));
        }
        return loading.then((error) => (error ? castFailureReason(error) : null), castFailureReason);
      };
      const fail = (reason) => {
        if (stale()) return;
        console.error("Cast failed", stage, castFailureReason(reason));
        // Never end a session that existed before this tap.
        if (!hadSession) session.endSession(true);
        this.finishCasting({ restoreLocal: true });
      };
      this.showCastPlaceholder(session);
      load(request).then(async (failure) => {
        if (stale()) return;
        // Captions must not break casting: retry once without the track.
        if (failure && hasTracks) {
          stage = "retry";
          failure = await load(buildRequest(false));
          if (stale()) return;
        }
        if (!failure) {
          this.castLoaded = true;
          return;
        }
        fail(failure);
      }).catch(fail);

      this.releaseWakeLock();
    } catch (error) {
      // Picker cancelled (framework code "cancel"), or a step above threw.
      if (stale()) return;
      const reason = castFailureReason(error);
      if (reason !== "cancel") console.error("Cast failed", stage, reason);
      this.finishCasting({ resume: false, restoreLocal: true });
    }
  }

  // Hide the browser player first so the video never shows under the overlay, then name the device.
  showCastPlaceholder(session) {
    if (this.hasCastPlaceholderTarget) this.castPlaceholderTarget.classList.remove("hidden");
    if (this.hasVideoContainerTarget) this.videoContainerTarget.classList.add("hidden");
    this.updateCastStatus(session);
  }

  castDeviceName(session) {
    return session?.getCastDevice?.()?.friendlyName?.trim() || "Cast device";
  }

  updateCastStatus(session) {
    if (!this.hasCastStatusTarget) return;
    this.castStatusTarget.textContent = `Casting to ${this.castDeviceName(session)}`;
  }

  // The default receiver builds text tracks from MediaInfo.tracks. The track is attached inactive
  // unless the viewer has captions on here. The receiver fetches the VTT cross-origin, so the
  // caption URL needs CORS headers. Skipped for live, and for relative or blob: URLs.
  attachCastCaptions(mediaInfo, request) {
    if (!CAST_CAPTIONS_ENABLED || this.isLive()) return;
    const { url, label, lang } = this.castCaptions();
    if (!/^https?:\/\//i.test(url)) return;
    const track = new chrome.cast.media.Track(1, chrome.cast.media.TrackType.TEXT);
    track.trackContentId = url;
    track.trackContentType = "text/vtt";
    track.subtype = chrome.cast.media.TextTrackType.SUBTITLES;
    track.name = label || "Captions";
    track.language = lang || "en";
    mediaInfo.tracks = [track];
    if (this.showingCaptionTrack(this.playerElement.store) !== null) request.activeTrackIds = [1];
  }

  // The template renders a static <track> for every non-live source (AWS included).
  castCaptions() {
    const el = this.media?.querySelector("track[kind='captions']");
    return { url: el?.src || "", label: el?.label, lang: el?.srclang };
  }

  // Seconds the TV should start from; 0 for live or an unusable value.
  localCastStartPosition() {
    if (this.isLive()) return 0;
    const position = this.isPremiere() ? this.castPremierePosition() : this.media.currentTime;
    return Number.isFinite(position) && position > 0 ? position : 0;
  }

  // Pause and mute the browser player for the whole cast; the play listener re-pauses it because
  // autoplay on canplay and other play() callers can restart it.
  silenceLocalPlayback() {
    this.castPreviousMuted = this.media.muted;
    this.pauseThroughStore();
    this.setMutedWithoutPersisting(true);
    this.pauseMiniAudioPlayer();
    if (!this.castPlayGuard) {
      this.castPlayGuard = () => {
        if (!this.isCasting) return;
        this.media.pause();
        this.pauseMiniAudioPlayer();
      };
      this.media.addEventListener("play", this.castPlayGuard);
    }
  }

  // Idempotent. Restores the browser player and, unless told otherwise, resumes it where the
  // receiver left off.
  finishCasting({ resume = true, restoreLocal = false } = {}) {
    if (!this.isCasting) return;
    this.isCasting = false;
    this.syncCastButtons();
    this.bumpCastAttempt();
    this.reportCastFinalProgress();
    this.clearCastReportTimer();
    const finishedOnReceiver = this.castFinishedReported && !this.isPremiere();

    if (this.castPlayGuard) {
      this.media.removeEventListener("play", this.castPlayGuard);
      this.castPlayGuard = null;
    }
    if (this.castPreviousMuted !== undefined) this.setMutedWithoutPersisting(this.castPreviousMuted);
    this.castPreviousMuted = undefined;

    if (this.hasCastPlaceholderTarget) this.castPlaceholderTarget.classList.add("hidden");
    if (this.hasVideoContainerTarget) this.videoContainerTarget.classList.remove("hidden");

    const airplayActive = this.airplaySwapped || this.media.webkitCurrentPlaybackTargetIsWireless;
    // Cancelled picker, failed load, or stopped before the load started: the TV never played, so put
    // the browser back as it was, no seek.
    if (restoreLocal || !this.castLoaded) {
      if (this.castLocalWasPlaying) this.playVideo();
      return;
    }
    if (!resume || airplayActive) return;

    if (!this.isLive()) {
      let target = finishedOnReceiver ? this.media.duration : this.castLastTime;
      if (this.isPremiere()) {
        target = this.castPremierePosition();
      } else if (Number.isFinite(target) && target > 0) {
        const duration = this.media.duration;
        if (Number.isFinite(duration)) target = Math.min(target, duration);
      } else {
        target = null;
      }
      if (target !== null && Number.isFinite(target)) this.seekThroughStore(target);
    }

    // Finished on the receiver: stay at the end, so the local seek cannot overwrite the saved end.
    if (finishedOnReceiver && !this.isLive()) return;
    if (!this.castRemotePaused || (this.isLive() && this.castLocalWasPlaying)) this.playVideo();
  }

  setupCastTracking() {
    this.remotePlayer = new cast.framework.RemotePlayer();
    this.remotePlayerController = new cast.framework.RemotePlayerController(this.remotePlayer);
    const events = cast.framework.RemotePlayerEventType;
    this.onRemoteTime = () => {
      const p = this.remotePlayer;
      if (this.isCasting && this.castLoaded && p.isConnected && p.isMediaLoaded && p.currentTime > 0) this.castLastTime = p.currentTime;
      this.reportRemoteProgress({ throttle: true });
    };
    this.onRemotePaused = () => {
      const p = this.remotePlayer;
      if (this.isCasting && this.castLoaded && p.isConnected && p.isMediaLoaded) this.castRemotePaused = p.isPaused;
      this.reportRemoteProgress({ isPlaying: !p.isPaused });
    };
    // Finished on the receiver: store the end of the item once. No supercast-playback-ended, which
    // would auto-advance the queue. Leaving IDLE (replay, seek back) re-arms it.
    this.onRemoteState = () => {
      if (this.isLive() || !(this.isCasting && this.castLoaded)) return;
      const p = this.remotePlayer;
      if (String(p.playerState).toUpperCase() !== "IDLE") {
        this.castFinishedReported = false;
        return;
      }
      if (this.castFinishedReported) return;
      const reason = this.castContext.getCurrentSession()?.getMediaSession()?.idleReason;
      const duration = this.castDuration();
      const finished = String(reason).toUpperCase() === "FINISHED" ||
        (duration > 0 && this.castLastTime >= duration - 2);
      if (!finished || !(duration > 0)) return;
      this.castFinishedReported = true;
      this.clearCastReportTimer();
      this.reportPlaybackProgress({ currentTime: duration, duration, isPlaying: false });
    };
    this.remotePlayerController.addEventListener(events.CURRENT_TIME_CHANGED, this.onRemoteTime);
    this.remotePlayerController.addEventListener(events.IS_PAUSED_CHANGED, this.onRemotePaused);
    this.remotePlayerController.addEventListener(events.PLAYER_STATE_CHANGED, this.onRemoteState);

    this.onSessionState = (event) => {
      if (event.sessionState === cast.framework.SessionState.SESSION_ENDED) this.finishCasting();
    };
    this.castContext.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, this.onSessionState);
  }

  teardownCastTracking() {
    if (this.castPlayGuard) this.media?.removeEventListener("play", this.castPlayGuard);
    this.clearCastReportTimer();
    if (!this.castContext || !this.remotePlayerController) return;
    const events = cast.framework.RemotePlayerEventType;
    this.remotePlayerController.removeEventListener(events.CURRENT_TIME_CHANGED, this.onRemoteTime);
    this.remotePlayerController.removeEventListener(events.IS_PAUSED_CHANGED, this.onRemotePaused);
    this.remotePlayerController.removeEventListener(events.PLAYER_STATE_CHANGED, this.onRemoteState);
    this.castContext.removeEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, this.onSessionState);
  }

  // Null when there is no payload url to fetch, the fetch fails, or the payload carries no cast_url.
  async fetchCastUrl() {
    if (!this.castPayloadUrlValue) return null;

    try {
      const response = await fetch(this.castPayloadUrlValue, { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!response.ok) return null;

      const payload = await response.json();
      // /live.json serializes whichever event the page resolves, which may not be this player's.
      if (this.hasCastEventIdValue && payload.id !== this.castEventIdValue) return null;
      return payload.cast_url || null;
    } catch (_error) {
      return null;
    }
  }

  async stopCasting() {
    const castSession = await this.getChromeCastSession(false);
    // Read the receiver position BEFORE ending the session.
    // Only once our own load succeeded: an autojoined session can still hold older media.
    const estimated = this.castLoaded ? castSession?.getMediaSession()?.getEstimatedTime() : null;
    if (Number.isFinite(estimated) && estimated > 0) this.castLastTime = estimated;
    if (castSession) {
      castSession.endSession(true);
    }
    // endSession also fires SESSION_ENDED; finishCasting is idempotent.
    this.finishCasting();
  }

  initChromecast() {
    // Live has no cast URL and renders no cast button.
    if (this.isLive()) { return; }
    if (!window.chrome) { return; }
    // Held so disconnect() can stop polling. chrome.cast loads after the SDK script tag does, so
    // poll for it rather than checking once.
    this.loadCastInterval = setInterval(() => {
      if (window.chrome.cast && window.chrome.cast.isAvailable) {
        this.stopCastPolling();
        this.initializeCastApi();
      }
    }, 1000);
  }

  stopCastPolling() {
    clearInterval(this.loadCastInterval);
    this.loadCastInterval = null;
  }

  async initializeCastApi() {
    this.castContext = cast.framework.CastContext.getInstance();
    this.castContext.setOptions({
      receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
    });
    this.setupCastTracking();
    this.castButtonTargets.forEach((button) => { button.hidden = false; });
  }

  async getChromeCastSession(initializeNew) {
    const castSession = this.castContext.getCurrentSession();
    if (castSession) {
      return castSession;
    } else if (initializeNew) {
      await this.castContext.requestSession();
      return this.castContext.getCurrentSession();
    } else {
      return null;
    }
  }


  // --- AirPlay ---

  // AirPlay hands the receiver the element's current URL, which needs cookies it can't send. For AWS
  // HLS premieres, swap to the cookie-free cast_url on a capture-phase click BEFORE the picker opens
  // ('connect' would be too late). It is prefetched so the click stays synchronous in the user
  // gesture; if not ready yet the click is a no-op. Native HLS only, as AirPlay can't use hls.js blobs.
  setupAirplayCastUrl() {
    if (!this.castPayloadUrlValue || !this.isPremiere() || !window.WebKitPlaybackTargetAvailabilityEvent || !this.nativeVideo) return;

    if (this.airplayListeners) return;

    const remote = this.nativeVideo.remote;
    this.airplayListeners = [
      [this.nativeVideo, "webkitplaybacktargetavailabilitychanged", (event) => {
        if (event.availability === "available" && !this.airplayCastUrl) {
          this.fetchCastUrl().then((url) => { this.airplayCastUrl = url; }).catch(() => {});
        }
      }],
      [this.element, "click", (event) => {
        if (event.target.closest?.("media-airplay-button")) this.swapToCastUrl();
      }, true],
      [remote, "disconnect", () => this.restoreFromCastUrl()],
      [this.nativeVideo, "webkitcurrentplaybacktargetiswirelesschanged", () => {
        if (!this.nativeVideo.webkitCurrentPlaybackTargetIsWireless) this.restoreFromCastUrl();
      }]
    ].filter(([target]) => target?.addEventListener);
    this.airplayListeners.forEach(([target, type, handler, capture]) => target.addEventListener(type, handler, capture));
  }

  teardownAirplayCastUrl() {
    this.airplayListeners?.forEach(([target, type, handler, capture]) => target.removeEventListener(type, handler, capture));
    this.airplayListeners = null;
  }

  swapToCastUrl() {
    if (!this.airplayCastUrl || this.airplaySwapped) return;

    // Empty when the element plays a MediaSource/blob source; restore then reloads normally.
    this.airplayOriginalSrc = this.nativeVideo.currentSrc || null;
    this.airplaySwapped = true;
    this.reloadVideoSource(this.airplayCastUrl);
  }

  restoreFromCastUrl() {
    if (!this.airplaySwapped) return;

    const original = this.airplayOriginalSrc;
    this.airplaySwapped = false;
    this.airplayOriginalSrc = null;
    if (original) {
      this.reloadVideoSource(original);
    } else {
      this.reloadVideoSource(this.hlsUrl());
    }
  }

  // Source swap keeping playhead and play state. The new engine counts as a new Mux view, not a
  // failure, and its speed reset is ignored. Restore goes back through hlsSource.
  reloadVideoSource(url) {
    const time = this.media.currentTime;
    const wasPlaying = !this.media.paused;
    this.media.addEventListener("loadedmetadata", () => {
      this.media.currentTime = time;
      if (wasPlaying) this.playVideo();
    }, { once: true });
    this.markSourceLoading();
    this.muxRemoveHls();
    this.media.source = this.hlsSource(url);
    this.muxSourceChange(muxBranchNativeHls);
    this.bindMuxHlsJs();
  }

  // --- Mini audio player ---

  // The persistent mini audio player outlives navigation: once a video really starts, silence it.
  setupMiniAudioInterop() {
    this.media.addEventListener("play", this.pauseMiniAudioPlayer);
  }

  // Arrow property so the same reference can be removed on disconnect.
  pauseMiniAudioPlayer = () => {
    const audio = getAudioPlayerController(this.application);
    if (audio && !audio.paused) audio.pause();
  };

  // --- Picture-in-Picture ---

  // <hlsjs-video> is not a media element: the real <video> (the one Picture-in-Picture and Mux
  // work with) is its open-shadow-root target. document.pictureInPictureElement is retargeted to
  // the host across that shadow boundary, so both are compared.
  get nativeVideo() {
    return this.pipVideo || this.media?.target || null;
  }

  inPictureInPicture() {
    const active = document.pictureInPictureElement;
    return Boolean(active) && (active === this.media || active === this.nativeVideo);
  }

  setupPictureInPicture() {
    if (!document.pictureInPictureEnabled) return;

    this.pipVideo = this.media.target;
  }

  exitPictureInPictureForNavigation() {
    if (!this.inPictureInPicture()) return;

    document.exitPictureInPicture?.()?.catch(() => {});
  }

  // --- Mux Data ---

  // Mux monitors the real <video> inside <hlsjs-video> (mux-embed rejects any other tag). hls.js is
  // bound only when the engine exists: it is null on native HLS (WebKit). The engine is built a tick
  // after the source is assigned, so the branch is settled once that has run.
  startMuxMonitoring(branch = muxBranchNativeHls) {
    if (this.muxAttached || !this.media?.target) return;

    this.muxAttached = true;
    this.muxBranch = branch;
    mux.monitor(this.media.target, { debug: false, data: this.muxMetadata() });

    if (branch !== muxBranchHlsJs && branch !== muxBranchProgressive) this.bindMuxHlsJs();
  }

  async bindMuxHlsJs() {
    const media = this.media;
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (!this.muxAttached || this.media !== media || this.muxBranch !== muxBranchNativeHls) return;

    const engine = media.engine;
    if (!engine) return;

    this.muxBranch = muxBranchHlsJs;
    mux.addHLSJS(media.target, { hlsjs: engine, Hls: engine.constructor });
  }

  // Shared by monitor() and every videochange - an empty videochange payload drops the view's
  // metadata (including startup timing), per mux-embed's docs.
  muxMetadata() {
    return {
      env_key: this.muxEnvKeyValue,
      player_name: "Supercast Video Player",
      player_init_time: this.muxPlayerInitTime,
      // Generated server-side by Playback::VideoId ("episode:123"). Keep the spelling stable: the
      // Mux history already recorded under it is the reason, not a contract with any other client.
      video_id: this.videoIdValue,
      video_title: this.videoTitleValue,
      video_stream_type: this.streamTypeValue,
      video_cdn: this.cdnValue,
      video_content_type: this.isLive() ? "livestream" : "episode",
      view_client_application_name: "Supercast (Web)",
      ...this.muxObservabilityData()
    };
  }

  // :playback_observability extras. Empty with the flag off, so the payload is then exactly the
  // base fields. Mux labels custom dimensions by number only, so the mapping lives here: do not
  // renumber (Mux stores them positionally).
  //   custom_1 - subscription plan tier ("none" for followers/logged-out viewers)
  //   custom_2 - token type: "aws_hls", "bunny" or "ivs"
  //   custom_3 - client surface: "native" or "web"
  muxObservabilityData() {
    if (!this.observabilityValue) return {};

    try {
      const data = {
        sub_property_id: this.subPropertyIdValue,
        custom_1: this.planTierValue,
        custom_2: this.tokenType(),
        custom_3: this.clientSurface()
      };

      // Absent, not empty: an empty viewer_user_id would merge every logged-out viewer into one person.
      if (this.viewerIdValue) data.viewer_user_id = this.viewerIdValue;

      return data;
    } catch {
      return {};
    }
  }

  tokenType() {
    if (this.hasTokenTypeValue && this.tokenTypeValue) return this.tokenTypeValue;
    if (this.isLive()) return "ivs";
    return this.awsHlsValue ? "aws_hls" : "bunny";
  }

  clientSurface() {
    return document.querySelector("[data-hotwire-native]") ? "native" : "web";
  }

  // An intentional source swap. Mux treats this as a new view rather than a failure of the old one.
  muxSourceChange(branch) {
    if (!this.muxAttached || !this.media?.target) return;

    this.muxBranch = branch;
    mux.emit(this.media.target, "videochange", this.muxMetadata());
  }

  muxRemoveHls() {
    if (!this.muxAttached || this.muxBranch !== muxBranchHlsJs || !this.media?.target) return;

    mux.removeHLSJS(this.media.target);
  }

  // mux.destroyMonitor() emits 'destroy' and unbinds mux's own element listeners.
  muxTeardown() {
    if (!this.muxAttached) return;

    this.muxAttached = false;
    const target = this.nativeVideo;
    if (target) mux.destroyMonitor(target);
  }

  // timestampValue is set too so a seek issued before metadata loads isn't overwritten by applyTimestamp.
  seekTo(seconds) {
    this.timestampValue = seconds;
    if (!this.ready) return;

    this.media.currentTime = seconds;
    this.playVideo();
  }

  // play() rejects when the browser blocks autoplay or a new load interrupts it. The player's own
  // play button covers both, so only those two are expected; anything else still surfaces.
  playVideo() {
    if (this.isCasting || this.premiereHeld()) return;

    const playing = this.media?.play();
    if (!playing) return;
    playing.catch((error) => {
      if (error && (error.name === "NotAllowedError" || error.name === "AbortError")) return;
      throw error;
    });
  }
}
